<template>
  <div v-if="hasListerContact">
    <h2>{{ t('listerDetails.contactPhone.title') }}</h2>
    <p v-if="listerPerson" :class="$style.person">
      {{ listerPerson }}
    </p>

    <PhoneNumber
      v-if="phoneNumber"
      :label="t('listerDetails.phoneButton.label')"
      :phoneNumber="phoneNumber"
      :revealBlocked="isPhoneNumberRevealBlocked"
      @mobileNumberClick="emit('phoneNumberClickMobile', PhoneNumberType.LISTER_PHONE)"
      @numberClick="emit('revealedPhoneNumberClick', PhoneNumberType.LISTER_PHONE)"
      @numberReveal="emit('phoneNumberReveal', PhoneNumberType.LISTER_PHONE)"
      @contactPhoneBlock="emit('phoneNumberRevealBlock', PhoneNumberType.LISTER_PHONE)"
    />

    <PhoneNumber
      v-if="mobileNumber"
      :label="t('listerDetails.phoneButton.label')"
      :phoneNumber="mobileNumber"
      :revealBlocked="isPhoneNumberRevealBlocked"
      @mobileNumberClick="emit('phoneNumberClickMobile', PhoneNumberType.LISTER_MOBILE)"
      @numberClick="emit('revealedPhoneNumberClick', PhoneNumberType.LISTER_MOBILE)"
      @numberReveal="emit('phoneNumberReveal', PhoneNumberType.LISTER_MOBILE)"
      @contactPhoneBlock="emit('phoneNumberRevealBlock', PhoneNumberType.LISTER_MOBILE)"
    />
  </div>
</template>

<script lang="ts" setup>
import { Lister } from '@smg-real-estate/hg-rets-schema';
import { PhoneNumber } from '@smg-real-estate/seeker-app-shared-components';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { PhoneNumberType } from '../ContactForm/contact-form-tracking-events';

const { t } = useI18n();

defineOptions({
  name: 'ListerContactPhone',
});

interface Props {
  lister: Lister;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  phoneNumberClickMobile: [phoneNumberType: PhoneNumberType];
  revealedPhoneNumberClick: [phoneNumberType: PhoneNumberType];
  phoneNumberReveal: [phoneNumberType: PhoneNumberType];
  phoneNumberRevealBlock: [phoneNumberType: PhoneNumberType];
}>();

const hasListerContact = computed((): boolean => {
  return !!listerPerson.value || !!phoneNumber.value || !!mobileNumber.value;
});
const listerPerson = computed((): string | undefined => {
  const givenName = props.lister.contacts?.inquiry?.givenName ?? '';
  const familyName = props.lister.contacts?.inquiry?.familyName ?? '';

  const fullName = `${givenName} ${familyName}`.trim();

  return fullName || undefined; // If both are empty, return undefined.
});
const phoneNumber = computed((): string | undefined => {
  return props.lister.phone;
});
const mobileNumber = computed((): string | undefined => {
  return props.lister.mobile;
});
const isPhoneNumberRevealBlocked = computed((): boolean => {
  return props.lister.allowToContact === false;
});
</script>

<style lang="scss" module>
@import '@/assets/variables';

.person {
  margin: 0 0 $space-100;
}
</style>
